import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { IMG, PRICE, Reveal, Overline, useInView } from './shared';
import { trackEvent } from '../../lib/analytics';
import { blogPosts } from '../../data/blogData';

export const WhyEditorial = () => (
  <section id="why-harpalyk" className="bg-white text-gray-900 py-28 md:py-40 px-6 md:px-12" data-testid="hv2-why">
    <div className="max-w-5xl mx-auto text-center">
      <Reveal><Overline>Why Harpalyk</Overline></Reveal>
      <Reveal delay={100}>
        <h2 className="font-outfit text-5xl md:text-7xl font-medium leading-[1.05] tracking-tighter">
          ENGINEERED<br /><span className="text-gray-300">FOR</span><br />INDIA.
        </h2>
      </Reveal>
      <Reveal delay={200}>
        <p className="font-manrope text-lg text-gray-500 mt-10 max-w-2xl mx-auto leading-relaxed">
          Built for Indian homes and floor types — everyday dust, marble, tile and monsoon humidity. Residential and
          commercial robots with free installation, 2-year warranty, genuine spare parts and pan-India service support.
        </p>
      </Reveal>
    </div>
  </section>
);

const GUIDE_KEYWORDS = ['buying guide', 'lidar', 'self-empty'];
export const GuidesEditorial = () => {
  const guides = [];
  for (const kw of GUIDE_KEYWORDS) {
    const found = blogPosts.find((p) => p.title.toLowerCase().includes(kw) && !guides.includes(p));
    if (found) guides.push(found);
  }
  for (const p of blogPosts) {
    if (guides.length >= 3) break;
    if (!guides.includes(p)) guides.push(p);
  }
  return (
    <section className="bg-[#f5f5f7] text-gray-900 py-28 md:py-36 px-6 md:px-12" data-testid="hv2-guides">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <h2 className="font-outfit text-4xl md:text-6xl font-medium tracking-tighter mb-16">LEARN BEFORE YOU CHOOSE.</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-10">
          {guides.slice(0, 3).map((post, i) => (
            <Reveal key={post.slug} delay={i * 100}>
              <Link to={`/blog/${post.slug}`} onClick={() => trackEvent('homepage_guide_click')} data-testid={`hv2-guide-${i}`} className="group block">
                <div className="overflow-hidden">
                  <img src={post.image} alt={post.title} loading="lazy" decoding="async" className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <p className="font-manrope text-xs uppercase tracking-[0.25em] font-bold text-[#0057B8] mt-6">{post.category}</p>
                <h3 className="font-outfit text-xl md:text-2xl font-medium tracking-tight mt-2 group-hover:text-[#0057B8] transition-colors">{post.title}</h3>
              </Link>
            </Reveal>
          ))}
        </div>
        <Reveal delay={300}>
          <Link to="/blog" onClick={() => trackEvent('homepage_guide_click')} className="font-manrope inline-flex items-center gap-2 mt-14 text-sm font-bold tracking-[0.2em] text-gray-900 hover:text-[#0057B8] transition-colors">
            EXPLORE BUYING GUIDES <ArrowRight className="w-4 h-4" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const FAQS = [
  { q: 'Are robot vacuums good for Indian homes?', a: 'Yes. Modern floor cleaning robots are built for Indian conditions — high dust levels, marble and tile floors, thresholds and pet hair. Models with 3,000Pa+ suction and mopping handle daily Indian cleaning reliably.' },
  { q: 'Can a robot vacuum mop and vacuum at the same time?', a: 'Yes. Many Harpalyk floor robots vacuum and mop simultaneously in a single pass, and automatically avoid carpets while mopping.' },
  { q: 'Can robot vacuums clean marble and tiled floors?', a: 'Absolutely. Rubberized brushes and controlled-water mopping clean marble, granite, tile and wooden floors safely without scratching or streaking.' },
  { q: 'How much does a floor cleaning robot cost in India?', a: 'Harpalyk floor cleaning robots range from ₹19,990 for capable entry models to ₹79,990 for premium self-cleaning models. EMI options are available at checkout.' },
  { q: 'Do robot vacuums work on carpets and pet hair?', a: 'Yes. Choose a model with 3,000Pa+ suction, carpet detection and anti-tangle brushes — suction boosts automatically on carpets and HEPA filtration captures allergens and pet dander.' },
  { q: 'How often does a robot vacuum need maintenance?', a: 'Very little — empty the dustbin weekly (self-emptying models need it only every few weeks), clean brushes monthly and replace filters every 6 months with genuine Harpalyk parts.' },
  { q: 'What warranty and support does Harpalyk provide?', a: 'Robots come with a 1 or 2-year warranty depending on the model, free installation, pan-India service support and genuine spare parts.' },
  { q: 'How can I book a demonstration?', a: 'Use the Book a Demo button or the contact form below — or WhatsApp us at +91-7011370322 and our team will arrange a free demonstration.' },
];

export const LuminexFaq = () => {
  const [open, setOpen] = useState(null);
  return (
    <section className="bg-white text-gray-900 py-28 md:py-36 px-6 md:px-12" data-testid="hv2-faq">
      <div className="max-w-3xl mx-auto">
        <Reveal>
          <h2 className="font-outfit text-4xl md:text-5xl font-medium tracking-tighter mb-14">QUESTIONS, ANSWERED.</h2>
        </Reveal>
        <div>
          {FAQS.map((f, i) => (
            <div key={f.q} className="border-b border-black/10">
              <button onClick={() => setOpen(open === i ? null : i)} data-testid={`hv2-faq-${i}`}
                className="w-full flex items-center justify-between py-6 text-left group">
                <span className="font-outfit text-lg md:text-xl font-medium tracking-tight group-hover:text-[#0057B8] transition-colors">{f.q}</span>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`} />
              </button>
              {open === i && <p className="font-manrope text-gray-500 leading-relaxed pb-6 pr-8">{f.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const FinalCta = ({ onBuy }) => (
  <section className="bg-[#0a0a0a] text-white py-28 md:py-40 px-6 md:px-12 text-center overflow-hidden" data-testid="hv2-final">
    <div className="max-w-4xl mx-auto">
      <Reveal>
        <img src={IMG.robotDock} alt="Harpalyk LumineX 300 robot vacuum with self-emptying dock" loading="lazy" decoding="async" className="w-full max-w-md mx-auto rounded-3xl shadow-2xl shadow-black/50" />
      </Reveal>
      <Reveal delay={100}>
        <h2 className="font-outfit text-4xl md:text-6xl font-medium tracking-tighter mt-12">READY FOR A CLEANER<br />WAY TO LIVE?</h2>
      </Reveal>
      <Reveal delay={200}>
        <p className="font-outfit text-2xl mt-8">LumineX 300</p>
        <p className="font-oswald text-4xl font-medium mt-2">{PRICE}</p>
        <p className="font-manrope text-sm text-white/50 mt-3 tracking-wide">15,000Pa | LiDAR + AI | Auto-Empty</p>
      </Reveal>
      <Reveal delay={300}>
        <div className="flex flex-wrap justify-center gap-4 mt-10">
          <button onClick={onBuy} data-testid="hv2-final-buy-btn" className="font-manrope bg-[#0057B8] hover:bg-[#004494] text-white px-10 py-4 rounded-full font-semibold transition-colors">BUY NOW</button>
          <a href="#contact" onClick={() => trackEvent('homepage_luminex_demo')} data-testid="hv2-final-demo-btn" className="font-manrope border border-white/25 hover:bg-white hover:text-black px-10 py-4 rounded-full font-semibold transition-colors">BOOK A DEMO</a>
        </div>
        <a href="https://wa.me/917011370322" target="_blank" rel="noopener noreferrer" className="font-manrope inline-block mt-8 text-sm text-white/50 hover:text-white underline underline-offset-4 transition-colors">
          Talk to a Product Expert
        </a>
      </Reveal>
    </div>
  </section>
);

export const StickyBuyBar = ({ onBuy }) => {
  const [heroRef, heroPassed] = useInView(0.01);
  return (
    <>
      <span ref={heroRef} className="absolute top-[110vh]" aria-hidden="true" />
      {heroPassed && (
        <div className="fixed bottom-14 inset-x-0 z-40 md:hidden px-3" data-testid="hv2-sticky-buy">
          <div className="flex items-center justify-between gap-3 bg-black/80 backdrop-blur-xl text-white rounded-full px-5 py-2.5 border border-white/10">
            <div className="min-w-0">
              <p className="font-outfit text-sm font-medium truncate">LumineX 300</p>
              <p className="font-oswald text-sm text-[#00AEEF]">{PRICE}</p>
            </div>
            <button onClick={onBuy} data-testid="hv2-sticky-buy-btn" className="font-manrope bg-[#0057B8] text-white text-sm font-semibold px-5 py-2 rounded-full flex-shrink-0">
              BUY NOW
            </button>
          </div>
        </div>
      )}
    </>
  );
};
